import { ChangeDetectionStrategy, Component, computed, inject, model, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ComplexArticleBlueprintFactory } from '@admintool/article/generate-article/complex-article-blueprints/services/complex-article-blueprint.factory';
import { ComplexArticleBlueprintDataService } from '@admintool/article/generate-article/complex-article-blueprints/services/complex-article-blueprint.data.service';
import { ComplexArticleBlueprintService } from '@admintool/article/generate-article/complex-article-blueprints/services/complex-article-blueprint.service';
import { ComplexArticleBlueprintDTO } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprint';
import { AdmintoolComponentsPresenter } from '@admintool/components/presenter/admintool-components.presenter';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { Log } from '@shared/debug/log';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { TooltipDirective } from '@shared/tooltip/tooltip.directive';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-complex-article-blueprint-list',
  imports: [
    TranslateModule,
    SvgIconComponent,
    TooltipDirective,
    FormsModule
  ],
  templateUrl: './complex-article-blueprint-list.component.html',
  styleUrl: './complex-article-blueprint-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ComplexArticleBlueprintListComponent implements OnInit {
  private readonly complexArticleBlueprintRuleFactory = inject(ComplexArticleBlueprintFactory);
  private readonly complexArticleBlueprintDataService = inject(ComplexArticleBlueprintDataService);
  private readonly complexArticleBlueprintService = inject(ComplexArticleBlueprintService);
  private readonly admintoolComponentsPresenter = inject(AdmintoolComponentsPresenter);
  private readonly alertHelper = inject(AlertHelper);

  public selected = model<ComplexArticleBlueprintDTO>()
  public update = output();

  public allBlueprints = signal<ComplexArticleBlueprintDTO[]>([]);
  public search = signal<string>('');

  public filteredBlueprints = computed(() => {
    const search = this.search().trim();
    if(search.length == 0) {
      return this.allBlueprints();
    }
    return this.allBlueprints()
      .filter(blueprint => blueprint.name.toLowerCase().includes(search.toLowerCase()));
  });

  public ngOnInit() {
    this.reloadList();
  }

  public async reloadList() {
    const list = await firstValueFrom(this.complexArticleBlueprintDataService.getAll())
    this.allBlueprints.set(list)
  }

  public selectBlueprint(id: number): void {
    this.selected.set(this.allBlueprints().find(blueprint => blueprint.id == id));
  }

  public async newBlueprint(): Promise<void> {
    const newBlueprint = this.complexArticleBlueprintRuleFactory.newBlueprint('New Blueprint');
    const savedBlueprint = await firstValueFrom(this.complexArticleBlueprintDataService.create(newBlueprint));
    await firstValueFrom(this.complexArticleBlueprintDataService.save(savedBlueprint));
    if(Log.assertExists(savedBlueprint?.id)) {
      this.selectBlueprint(savedBlueprint.id ?? -1);
      await this.editBlueprint(savedBlueprint);
    }
    await this.reloadList();
  }

  protected async editBlueprint(blueprint: ComplexArticleBlueprintDTO): Promise<void> {
    const savedBlueprint = await this.admintoolComponentsPresenter.showEditComplexArticleRulesModal(structuredClone(blueprint));
    if(!savedBlueprint) {
      return;
    }
    this.allBlueprints.update((blueprints) => {
      const idx = blueprints.findIndex(b => b.id === savedBlueprint.id);
      if(idx === -1) return blueprints;
      blueprints[idx] = savedBlueprint;
      return structuredClone(blueprints);
    });
    this.selected.set(savedBlueprint);
    this.update.emit();
  }

  public async importBlueprint(): Promise<void> {
    const createdBlueprint = await this.complexArticleBlueprintService.importBlueprint();
    if (!createdBlueprint) {
      return;
    }
    if (Log.assertExists(createdBlueprint?.id)) {
      this.selectBlueprint(createdBlueprint.id ?? -1);
    }
    await this.reloadList();
  }

  public exportBlueprint(): void {
    const blueprint = this.selected();
    if(!blueprint) return;
    this.complexArticleBlueprintService.exportBlueprint(blueprint);
  }

  public async deleteBlueprint(blueprint: ComplexArticleBlueprintDTO, event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    if (await this.alertHelper.showAlert('complexArticleBlueprint.title.confirm_delete', blueprint.name, undefined,
      [AlertButton.CANCEL, AlertButton.OK]) !== AlertButton.OK) {
      return;
    }
    if (Log.assertExists(blueprint?.id)) {
      await firstValueFrom(this.complexArticleBlueprintDataService.delete(blueprint.id));
    }
    this.allBlueprints.update((blueprints) => {
      const idx = blueprints.findIndex(b => b.id === blueprint.id);
      blueprints.splice(idx, 1);
      return structuredClone(blueprints);
    });
    this.reloadList();
  }
}
